import { useEffect } from 'react';

import { collectExpandableNodeIds } from '~/settings/components/ConfigurationsPage/helpers/treeDataLoader';

import type { TreeInstance } from '@headless-tree/core';
import type { ConfigTreeNode } from '~/settings/components/ConfigurationsPage/types';

/**
 * Expand every loaded node while a search is active, so matches nested under the
 * account/cluster levels are actually rendered — `tree.getItems()` only returns items
 * that are currently visible.
 *
 * Deliberately not `tree.expandAll()`: that calls `item.expand()` on every folder
 * unconditionally, and `expand()` appends to `expandedItems` without a dedupe check and
 * rebuilds the tree each time. Since this re-runs on every page of results, that would
 * grow `expandedItems` without bound and cost one full rebuild per folder. Writing the
 * id set once is idempotent and rebuilds once.
 *
 * Nothing is collapsed when the search clears.
 */
export function useExpandAllOnSearch(
  tree: TreeInstance<ConfigTreeNode>,
  isSearchActive: boolean,
  treeData: ConfigTreeNode | null
) {
  useEffect(() => {
    if (!isSearchActive || !treeData) {
      return;
    }

    try {
      tree.applySubStateUpdate('expandedItems', collectExpandableNodeIds(treeData));
      tree.rebuildTree();
    } catch (err) {
      console.debug('Tree expand-all on search:', err);
    }
  }, [tree, isSearchActive, treeData]);
}
